// Debug switch for testing individual live API routes while the rest of the catalog keeps running
// from the offline export. Motivated by the current backend state: only /catalog/article is
// deployed, so "everything or nothing" would mean not being able to try it at all.
//
//   ?apitest=article           - article detail from the API, everything else offline
//   ?apitest=article,tree      - several routes, comma separated
//   ?apitest=all               - every route from the API (same as VITE_CATALOG_SOURCE=rest)
//   ?apitest=                  - empty value clears a stored selection again
//
// The selection is kept in sessionStorage so it survives client-side navigation (the query string
// is gone after the first route change) and disappears when the tab closes - a debug aid should
// never quietly outlive the session it was switched on in.

const STORAGE_KEY = 'pih.apitest';
const ALL = 'all';

/**
 * Route names accepted in the query string - the API path segments, so the switch reads like the
 * URL it produces. Covers every operation in the published OpenAPI contract
 * (pih-backend.dev.doka.com): Article, AttributeMapping, Config, detail-layout, Documents, Index,
 * Labels, Search-Index, System, Thumbnails, Tree - plus our own `asset`/`asset/tn`, which the
 * contract doesn't define. `thumbnails` and `search-index` have no dedicated CatalogProvider
 * method of their own (see apiTestPatches below) - both are only ever consumed merged into an
 * index item, so switching them patches the same `getIndex` as `index` does.
 */
export const API_TEST_ROUTE_NAMES = [
    'article',
    'system',
    'index',
    'thumbnails',
    'search-index',
    'tree',
    'config',
    'detail-layout',
    'attribute-mapping',
    'labels',
    'documents',
    'asset'
] as const;

export type ApiTestRoute = (typeof API_TEST_ROUTE_NAMES)[number];

const isKnownRoute = (value: string): value is ApiTestRoute => (API_TEST_ROUTE_NAMES as readonly string[]).includes(value);

const parse = (raw: string): ApiTestRoute[] => {
    const requested = raw
        .split(',')
        .map((entry) => entry.trim().toLowerCase())
        .filter((entry) => entry.length > 0);

    if (requested.includes(ALL)) {
        return [...API_TEST_ROUTE_NAMES];
    }

    const unknown = requested.filter((entry) => !isKnownRoute(entry));
    if (unknown.length > 0) {
        // eslint-disable-next-line no-console
        console.warn(
            `apitest: unknown route(s) ${unknown.join(', ')} - known values are ${API_TEST_ROUTE_NAMES.join(', ')}, or "all".`
        );
    }

    return requested.filter(isKnownRoute);
};

const read = (): ApiTestRoute[] => {
    if (typeof window === 'undefined') {
        return [];
    }

    const fromQuery = new URLSearchParams(window.location.search).get('apitest');

    // A present-but-empty ?apitest= is an explicit "switch it off", not "no parameter given" -
    // otherwise a stored selection could only be cleared by closing the tab.
    if (fromQuery !== null) {
        const parsed = parse(fromQuery);
        window.sessionStorage.setItem(STORAGE_KEY, parsed.join(','));
        return parsed;
    }

    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    return stored ? parse(stored) : [];
};

export const apiTestRoutes: ReadonlySet<ApiTestRoute> = new Set(read());

export const isApiTestActive = apiTestRoutes.size > 0;

if (isApiTestActive) {
    // eslint-disable-next-line no-console
    console.info(`apitest active - these routes are served by the live API: ${[...apiTestRoutes].join(', ')}`);
}
